import type { WebinarCta, WebinarPurchaseEvent } from "@/hooks/use-webinar-realtime";
import { formatCurrency } from "@/lib/i18n";

interface OfferOverlayProps {
  cta: WebinarCta | null;
  now: number;
  recentPurchases: WebinarPurchaseEvent[];
  onDismiss: () => void;
  onTrackClick: (cta: WebinarCta) => void | Promise<void>;
}

export function OfferOverlay({ cta, now, recentPurchases, onDismiss, onTrackClick }: OfferOverlayProps) {
  if (!cta) {
    return null;
  }

  return (
    <>
      {recentPurchases[0] ? (
        <div className="absolute right-6 top-6 z-20">
          <PurchaseToast purchase={recentPurchases[0]} />
        </div>
      ) : null}

      <div className="absolute bottom-6 right-6 z-10 w-full max-w-sm rounded-2xl border border-white/10 bg-black/85 p-4 text-white shadow-2xl backdrop-blur animate-in slide-in-from-bottom-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-primary/80">Live offer</p>
            <h2 className="mt-2 text-lg font-semibold">{cta.headline ?? cta.label}</h2>
            {cta.description ? (
              <p className="mt-2 text-sm text-gray-300">{cta.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-full border border-white/10 px-2 py-1 text-xs text-gray-300 hover:text-white"
          >
            Close
          </button>
        </div>

        {cta.productImageUrl ? (
          <img
            src={cta.productImageUrl}
            alt={cta.label}
            className="mt-4 h-36 w-full rounded-xl object-cover"
          />
        ) : null}

        {(cta.offerPriceCents != null || cta.originalPriceCents != null) ? (
          <div className="mt-4 flex items-end gap-3">
            {cta.offerPriceCents != null ? (
              <span className="text-2xl font-semibold text-white">
                {formatCurrency(cta.offerPriceCents, cta.currency ?? "EUR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            ) : null}
            {cta.originalPriceCents != null ? (
              <span className="text-sm text-gray-400 line-through">
                {formatCurrency(cta.originalPriceCents, cta.currency ?? "EUR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {cta.countdownEndsAt ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gray-400">Countdown</p>
              <p className="mt-1 text-lg font-semibold text-white">{formatCountdown(cta.countdownEndsAt, now)}</p>
            </div>
          ) : null}
          {cta.scarcityCount != null ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gray-400">Availability</p>
              <p className="mt-1 text-lg font-semibold text-white">{cta.scarcityCount} left</p>
            </div>
          ) : null}
        </div>

        {cta.bonuses?.length ? (
          <div className="mt-4 space-y-2">
            {cta.bonuses.map((bonus) => (
              <div key={bonus.name} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-200">
                <span>{bonus.name}</span>
                <span>{formatCurrency(bonus.valueCents, cta.currency ?? "EUR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        ) : null}

        {cta.testimonials?.length ? (
          <div className="mt-4 space-y-2">
            {cta.testimonials.slice(0, 2).map((testimonial) => (
              <blockquote key={`${testimonial.name}-${testimonial.text}`} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-sm text-gray-200">“{testimonial.text}”</p>
                <footer className="mt-2 text-xs uppercase tracking-[0.16em] text-gray-400">{testimonial.name}</footer>
              </blockquote>
            ))}
          </div>
        ) : null}

        {cta.url ? (
          <a
            href={cta.url}
            target="_blank"
            rel="noreferrer"
            onClick={() => void onTrackClick(cta)}
            className="mt-4 inline-flex rounded-full bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
          >
            Open offer
          </a>
        ) : null}
      </div>
    </>
  );
}

function PurchaseToast({ purchase }: { purchase: WebinarPurchaseEvent }) {
  return (
    <div className="rounded-xl border border-emerald-500/20 bg-black/85 px-4 py-3 text-sm text-white shadow-xl backdrop-blur">
      <p className="font-medium">
        {purchase.buyerName}
        {purchase.location ? ` from ${purchase.location}` : ""} just purchased!
      </p>
    </div>
  );
}

function formatCountdown(countdownEndsAt: string, now: number) {
  const remainingMs = Math.max(0, new Date(countdownEndsAt).getTime() - now);
  const totalSeconds = Math.ceil(remainingMs / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
